import produce from 'immer';

import { createReducer, getErrorStatus, getInitialStatus, getPendingStatus } from '../utils';
import type { Category } from '../../../shared/issuesV2';
import type { GetAllFiltersResponse, SavedFilter } from '../../../shared/savedFilters';
import type { ReduxAction, ReduxActionResponse, SyncReduxAction } from '../../types';

import { actionTypes, asyncActionTypes } from './actions';
import type { SavedFiltersStore } from './types';

//  Common Redux async response type for the POST/PUT/DELETE requests
type SavedFilterResponse = ReduxActionResponse<SavedFilter, { category: Category }>;

export const initialState: SavedFiltersStore = {
  //  Network requests:
  asyncStatus: {
    getFilters: getInitialStatus(),
    saveOrDeleteFilter: getInitialStatus(),
  },

  //  Represents the list of saved filters.
  filters: {
    licensing: [],
    vulnerability: [],
    quality: [],
  },

  //  Represents a saved filter which the user has selected and has been "loaded".
  currentFilter: null,
};

const setHasChangedReducer = {
  [actionTypes.sync.SET_HAS_CHANGED]: (state: SavedFiltersStore, action: SyncReduxAction<{ hasChanged: boolean }>) =>
    produce(state, (draftState) => {
      draftState.hasChanged = action.hasChanged;
    }),
};

const setCurrentFilterReducer = {
  [actionTypes.sync.SET_CURRENT_FILTER]: (
    state: SavedFiltersStore,
    action: SyncReduxAction<{ currentFilter: number | null }>,
  ) =>
    produce(state, (draftState) => {
      draftState.currentFilter = action.currentFilter;
    }),
};

const getAllFiltersReducer = {
  [asyncActionTypes.GET_FILTERS.PENDING]: (
    state: SavedFiltersStore,
    action: SyncReduxAction<{ meta: { category: Category } }>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getFilters = getPendingStatus();
      if (action.meta.category) {
        const { category } = action.meta;
        //  Reset the current list of filters, if the exist
        draftState.filters[category] = initialState.filters[category];
      }
    }),
  [asyncActionTypes.GET_FILTERS.FULFILLED]: (
    state: SavedFiltersStore,
    action: ReduxActionResponse<GetAllFiltersResponse, { category: Category }>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getFilters = getInitialStatus();
      if (action.meta?.category) {
        const { category } = action.meta;
        draftState.filters[category] = action.payload.data ?? [];
      }
    }),
  [asyncActionTypes.GET_FILTERS.REJECTED]: (state: SavedFiltersStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getFilters = getErrorStatus(action.payload);
    }),
};

const saveFilterAsReducer = {
  [asyncActionTypes.SAVE_FILTER_AS.PENDING]: (state: SavedFiltersStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.saveOrDeleteFilter = getPendingStatus();
    }),
  [asyncActionTypes.SAVE_FILTER_AS.FULFILLED]: (state: SavedFiltersStore, action: SavedFilterResponse) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.saveOrDeleteFilter = getInitialStatus();
      const savedFilter = action.payload.data;
      //  The newly saved filter is returned from the BE, set
      if (savedFilter) {
        const category = action.meta?.category as Category;
        draftState.filters[category].push(savedFilter);
        draftState.currentFilter = savedFilter.id;
      }
    }),
  [asyncActionTypes.SAVE_FILTER_AS.REJECTED]: (state: SavedFiltersStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.saveOrDeleteFilter = getErrorStatus(action.payload);
    }),
};

const saveFilterReducer = {
  [asyncActionTypes.SAVE_FILTER.PENDING]: (state: SavedFiltersStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.saveOrDeleteFilter = getPendingStatus();
    }),
  [asyncActionTypes.SAVE_FILTER.FULFILLED]: (state: SavedFiltersStore, action: SavedFilterResponse) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.saveOrDeleteFilter = getInitialStatus();
      const savedFilter = action.payload.data;
      //  Update the saved filter in the list.
      if (savedFilter) {
        const category = action.meta?.category as Category;
        //  Update the current filter
        draftState.currentFilter = savedFilter.id;
        //  Update the filter in the filters list.
        draftState.filters[category] = draftState.filters[category].map((filter) =>
          filter.id !== savedFilter.id ? filter : savedFilter,
        );
      }
    }),
  [asyncActionTypes.SAVE_FILTER.REJECTED]: (state: SavedFiltersStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.saveOrDeleteFilter = getErrorStatus(action.payload);
    }),
};

const renameFilterReducer = {
  [asyncActionTypes.RENAME_FILTER.PENDING]: (state: SavedFiltersStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.saveOrDeleteFilter = getPendingStatus();
    }),
  [asyncActionTypes.RENAME_FILTER.FULFILLED]: (state: SavedFiltersStore, action: SavedFilterResponse) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.saveOrDeleteFilter = getInitialStatus();
      const renamedFilter = action.payload.data;
      if (renamedFilter) {
        const category = action.meta?.category as Category;
        draftState.filters[category] = draftState.filters[category].map((filter) => {
          if (filter.id !== renamedFilter.id) {
            return filter;
          }
          return {
            ...filter,
            name: renamedFilter.name,
          };
        });
      }
    }),
  [asyncActionTypes.RENAME_FILTER.REJECTED]: (state: SavedFiltersStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.saveOrDeleteFilter = getErrorStatus(action.payload);
    }),
};

const deleteFilterReducer = {
  [asyncActionTypes.DELETE_FILTER.PENDING]: (state: SavedFiltersStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.saveOrDeleteFilter = getPendingStatus();
    }),
  [asyncActionTypes.DELETE_FILTER.FULFILLED]: (state: SavedFiltersStore, action: SavedFilterResponse) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.saveOrDeleteFilter = getInitialStatus();
      const deletedFilter = action.payload.data;
      //  Remove the saved filter from to the list.
      if (deletedFilter) {
        const category = action.meta?.category as Category;
        draftState.filters[category] = draftState.filters[category].filter((filter) => filter.id !== deletedFilter.id);
      }
      //  Reset the currentFilter
      draftState.currentFilter = initialState.currentFilter;
    }),
  [asyncActionTypes.DELETE_FILTER.REJECTED]: (state: SavedFiltersStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.saveOrDeleteFilter = getErrorStatus(action.payload);
    }),
};

export default createReducer<SavedFiltersStore, any>(initialState, {
  ...setCurrentFilterReducer,
  ...getAllFiltersReducer,
  ...saveFilterReducer,
  ...saveFilterAsReducer,
  ...renameFilterReducer,
  ...deleteFilterReducer,
  ...setHasChangedReducer,
});
